import type { FormConfig } from '../config/types';
import { getLondonDateParts } from './londonTime';

export const REPORTING_PERIOD_QUARTERS = [
  'Quarter 1 (April-June)',
  'Quarter 2 (July-September)',
  'Quarter 3 (October-December)',
  'Quarter 4 (January-March)',
] as const;

export const REPORTING_QUARTER_TOKEN = '{{REPORTING_QUARTER}}';
export const REPORTING_PERIOD_LABEL_TOKEN = '{{REPORTING_PERIOD_LABEL}}';
export const REPORTING_PERIOD_TOKEN = '{{REPORTING_PERIOD}}';
export const SUBMISSION_DEADLINE_TOKEN = '{{SUBMISSION_DEADLINE}}';
export const CURRENT_REPORTING_YEAR_TOKEN = '{{CURRENT_REPORTING_YEAR}}';

const LEGACY_REPORTING_QUARTER = 'Quarter 4 (2025/26)';
const LEGACY_REPORTING_PERIOD = '01 January 2026 - 31 March 2026';
const LEGACY_SUBMISSION_DEADLINE = '30 April 2026';

type ReportingPeriodDefaultValue = 'auto' | 'auto-1';

type ReportPeriodValue = {
  quarter: string;
  year: string;
};

export type ReportingPeriodText = {
  reportingQuarter: string;
  reportingPeriod: string;
  submissionDeadline: string;
};

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

const toFinancialYearLabel = (startYear: number) =>
  `${startYear}-${String(startYear + 1).slice(-2)}`;

export const calculateCurrentReportingYear = (now = new Date()) => {
  const { year, month } = getLondonDateParts(now);
  return toFinancialYearLabel(month >= 4 ? year : year - 1);
};

const normalizeDefaultValue = (defaultValue?: string): ReportingPeriodDefaultValue =>
  defaultValue?.trim().toLowerCase() === 'auto-1' ? 'auto-1' : 'auto';

const getQuarterNumber = (quarter: string) => Number(quarter.match(/Quarter\s+([1-4])/)?.[1] || 0);

const parseFinancialYearStart = (year: string) => {
  const match = /^(\d{4})-\d{2}$/.exec(year.trim());
  return match ? Number(match[1]) : new Date().getFullYear();
};

const daysInMonth = (year: number, monthIndex: number) =>
  new Date(year, monthIndex + 1, 0).getDate();

const formatDate = (day: number, monthIndex: number, year: number) =>
  `${String(day).padStart(2, '0')} ${MONTHS[monthIndex]} ${year}`;

const getReportingPeriodRange = (quarter: string, year: string) => {
  const quarterNumber = getQuarterNumber(quarter) || 3;
  const financialYearStart = parseFinancialYearStart(year);
  const periodByQuarter: Record<
    number,
    { startMonth: number; startYear: number; endMonth: number; endYear: number }
  > = {
    1: { startMonth: 3, startYear: financialYearStart, endMonth: 5, endYear: financialYearStart },
    2: { startMonth: 6, startYear: financialYearStart, endMonth: 8, endYear: financialYearStart },
    3: { startMonth: 9, startYear: financialYearStart, endMonth: 11, endYear: financialYearStart },
    4: {
      startMonth: 0,
      startYear: financialYearStart + 1,
      endMonth: 2,
      endYear: financialYearStart + 1,
    },
  };

  return { quarterNumber, period: periodByQuarter[quarterNumber] || periodByQuarter[3] };
};

const parseReportPeriodValue = (value: unknown): ReportPeriodValue | undefined => {
  if (typeof value === 'string') {
    try {
      return parseReportPeriodValue(JSON.parse(value));
    } catch (_) {
      return undefined;
    }
  }
  if (!value || typeof value !== 'object') return undefined;

  const parsed = value as { quarter?: unknown; year?: unknown };
  if (typeof parsed.quarter !== 'string' || typeof parsed.year !== 'string') return undefined;
  return { quarter: parsed.quarter, year: parsed.year };
};

const formatOrdinalDay = (day: number) => {
  const remainder100 = day % 100;
  if (remainder100 >= 11 && remainder100 <= 13) return `${day}th`;
  if (day % 10 === 1) return `${day}st`;
  if (day % 10 === 2) return `${day}nd`;
  if (day % 10 === 3) return `${day}rd`;
  return `${day}th`;
};

export const calculateSelectedReportingPeriodHint = (value: unknown) => {
  const selection = parseReportPeriodValue(value);
  if (!selection || !getQuarterNumber(selection.quarter) || !/^\d{4}-\d{2}$/.test(selection.year)) {
    return { dateRange: '', quarter: '' };
  }

  const { quarterNumber, period } = getReportingPeriodRange(selection.quarter, selection.year);
  const endDay = daysInMonth(period.endYear, period.endMonth);
  return {
    dateRange: `${formatOrdinalDay(1)} ${MONTHS[period.startMonth]} to ${formatOrdinalDay(endDay)} ${MONTHS[period.endMonth]}`,
    quarter: `Quarter ${quarterNumber}`,
  };
};

export const calculateAutoReportPeriod = (
  defaultValue: string = 'auto',
  now = new Date(),
): ReportPeriodValue => {
  const mode = normalizeDefaultValue(defaultValue);
  const month = now.getMonth() + 1;
  let quarterNumber = 3;
  let yearStart = now.getFullYear();

  if (mode === 'auto-1') {
    if (month >= 3 && month <= 5) {
      quarterNumber = 4;
      yearStart = now.getFullYear() - 1;
    } else if (month >= 6 && month <= 8) {
      quarterNumber = 1;
    } else if (month >= 9 && month <= 11) {
      quarterNumber = 2;
    } else if (month <= 2) {
      quarterNumber = 3;
      yearStart = now.getFullYear() - 1;
    }
  } else if (month >= 4 && month <= 6) {
    quarterNumber = 4;
    yearStart = now.getFullYear() - 1;
  } else if (month >= 7 && month <= 9) {
    quarterNumber = 1;
  } else if (month >= 10 && month <= 12) {
    quarterNumber = 2;
  } else if (month <= 3) {
    quarterNumber = 3;
    yearStart = now.getFullYear() - 1;
  }

  return {
    quarter: REPORTING_PERIOD_QUARTERS[quarterNumber - 1] || '',
    year: toFinancialYearLabel(yearStart),
  };
};

export const calculateReportingPeriodText = (
  defaultValue: string = 'auto',
  now = new Date(),
): ReportingPeriodText => {
  const { quarter, year } = calculateAutoReportPeriod(defaultValue, now);
  const { quarterNumber, period } = getReportingPeriodRange(quarter, year);
  const deadlineMonth = (period.endMonth + 1) % 12;
  const deadlineYear = period.endMonth === 11 ? period.endYear + 1 : period.endYear;

  return {
    reportingQuarter: `Quarter ${quarterNumber} (${year.replace('-', '/')})`,
    reportingPeriod: `${formatDate(1, period.startMonth, period.startYear)} - ${formatDate(
      daysInMonth(period.endYear, period.endMonth),
      period.endMonth,
      period.endYear,
    )}`,
    submissionDeadline: formatDate(
      daysInMonth(deadlineYear, deadlineMonth),
      deadlineMonth,
      deadlineYear,
    ),
  };
};

export const replaceReportingPeriodTokens = (
  value: string,
  defaultValue: string = 'auto',
  now = new Date(),
) => {
  const replacements = calculateReportingPeriodText(defaultValue, now);
  const { quarter, year } = calculateAutoReportPeriod(defaultValue, now);
  const reportingPeriodLabel = `${quarter} ${year}`.trim();
  return value
    .split(CURRENT_REPORTING_YEAR_TOKEN)
    .join(calculateCurrentReportingYear(now))
    .split(REPORTING_PERIOD_LABEL_TOKEN)
    .join(reportingPeriodLabel)
    .split(REPORTING_QUARTER_TOKEN)
    .join(replacements.reportingQuarter)
    .split(LEGACY_REPORTING_QUARTER)
    .join(replacements.reportingQuarter)
    .split(REPORTING_PERIOD_TOKEN)
    .join(replacements.reportingPeriod)
    .split(LEGACY_REPORTING_PERIOD)
    .join(replacements.reportingPeriod)
    .split(SUBMISSION_DEADLINE_TOKEN)
    .join(replacements.submissionDeadline)
    .split(LEGACY_SUBMISSION_DEADLINE)
    .join(replacements.submissionDeadline);
};

export const resolveReportPeriodDefaultValue = (config: FormConfig): string => {
  for (const section of config.sections || []) {
    for (const subsection of section.subsections || []) {
      for (const page of subsection.pages || []) {
        for (const question of 'questions' in page ? page.questions || [] : []) {
          if (
            question.questionType === 'reportPeriod' &&
            typeof question.defaultValue === 'string'
          ) {
            return question.defaultValue;
          }
        }
      }
    }
  }
  return 'auto';
};

export const replaceReportingPeriodTokensInConfig = <T>(
  value: T,
  defaultValue: string,
  now = new Date(),
): T => {
  if (typeof value === 'string') {
    return replaceReportingPeriodTokens(value, defaultValue, now) as T;
  }
  if (Array.isArray(value)) {
    return value.map((item) => replaceReportingPeriodTokensInConfig(item, defaultValue, now)) as T;
  }
  if (value && typeof value === 'object') {
    const replaced = Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        key,
        replaceReportingPeriodTokensInConfig(item, defaultValue, now),
      ]),
    ) as Record<string, unknown>;
    if (
      replaced.questionType === 'reportPeriod' &&
      typeof replaced.defaultValue === 'string' &&
      ['auto', 'auto-1'].includes(replaced.defaultValue.trim().toLowerCase())
    ) {
      replaced.defaultValue = normalizeDefaultValue(defaultValue);
    }
    return replaced as T;
  }
  return value;
};
